import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './CakeAddProduct.css';

const CakeAddProduct = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    starting_price: '',
    egg_type: 'eggless',
    shape: '',
    cake_message_enabled: true,
    customization_enabled: false,
    availability: 'available',
  });

  const [variants, setVariants] = useState([
    {
      weight: '',
      price: '',
    },
  ]);

  const [flavors, setFlavors] = useState([
    '',
  ]);

  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  let vendor = {};

  try {
    vendor = JSON.parse(localStorage.getItem('cakeVendor')) || {};
  } catch (error) {
    vendor = {};
  }

  const categories = useMemo(
    () => [
      'Birthday Cake',
      'Anniversary Cake',
      'Photo Cake',
      'Designer Cake',
      'Wedding Cake',
      'Custom Cake',
      'Kids Cake',
      'Other',
    ],
    []
  );

  const shapes = useMemo(
    () => [
      'Round',
      'Heart',
      'Square',
      'Rectangle',
      'Custom',
    ],
    []
  );

  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : value,
    }));

    if (message) {
      setMessage('');
      setMessageType('');
    }
  };

  const handleVariantChange = (
    index,
    field,
    value
  ) => {
    setVariants((prev) =>
      prev.map((variant, i) =>
        i === index
          ? {
              ...variant,
              [field]: value,
            }
          : variant
      )
    );
  };

  const addVariant = () => {
    setVariants((prev) => [
      ...prev,
      {
        weight: '',
        price: '',
      },
    ]);
  };

  const removeVariant = (index) => {
    setVariants((prev) => {
      if (prev.length === 1) {
        return prev;
      }

      return prev.filter(
        (_, i) => i !== index
      );
    });
  };

  const handleFlavorChange = (
    index,
    value
  ) => {
    setFlavors((prev) =>
      prev.map((flavor, i) =>
        i === index
          ? value
          : flavor
      )
    );
  };

  const addFlavor = () => {
    setFlavors((prev) => [
      ...prev,
      '',
    ]);
  };

  const removeFlavor = (index) => {
    setFlavors((prev) => {
      if (prev.length === 1) {
        return prev;
      }

      return prev.filter(
        (_, i) => i !== index
      );
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setMessage(
        'Please select a valid image file.'
      );
      setMessageType('error');
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      setMessage(
        'Cake image must be under 3 MB.'
      );
      setMessageType('error');
      return;
    }

    setImageFile(file);

    const reader = new FileReader();

    reader.onload = () => {
      setImagePreview(reader.result);
    };

    reader.readAsDataURL(file);

    if (message) {
      setMessage('');
      setMessageType('');
    }
  };

  const removeImage = () => {
    setImagePreview('');
    setImageFile(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('cakeToken');
    localStorage.removeItem('cakeVendor');

    navigate('/cake/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setMessage('Cake name is required.');
      setMessageType('error');
      return;
    }

    if (!formData.category) {
      setMessage(
        'Please select cake category.'
      );
      setMessageType('error');
      return;
    }

    if (
      !formData.starting_price ||
      Number(formData.starting_price) <= 0
    ) {
      setMessage(
        'Please enter a valid starting price.'
      );
      setMessageType('error');
      return;
    }

    const cleanVariants = variants
      .filter(
        (variant) =>
          variant.weight.trim() &&
          variant.price !== ''
      )
      .map((variant) => ({
        weight: variant.weight.trim(),
        price: Number(variant.price),
      }));

    const cleanFlavors = flavors
      .map((flavor) =>
        flavor.trim()
      )
      .filter(Boolean);

    setLoading(true);
    setMessage('');
    setMessageType('');

    try {
      const token =
        localStorage.getItem('cakeToken');

      if (!token) {
        navigate('/cake/login');
        return;
      }

      const payload = new FormData();

      payload.append(
        'name',
        formData.name.trim()
      );

      payload.append(
        'category',
        formData.category
      );

      payload.append(
        'description',
        formData.description.trim()
      );

      payload.append(
        'starting_price',
        String(
          Number(formData.starting_price)
        )
      );

      payload.append(
        'egg_type',
        formData.egg_type
      );

      payload.append(
        'shape',
        formData.shape || ''
      );

      payload.append(
        'cake_message_enabled',
        String(
          formData.cake_message_enabled
        )
      );

      payload.append(
        'customization_enabled',
        String(
          formData.customization_enabled
        )
      );

      payload.append(
        'availability',
        formData.availability
      );

      payload.append(
        'variants',
        JSON.stringify(cleanVariants)
      );

      payload.append(
        'flavors',
        JSON.stringify(cleanFlavors)
      );

      if (imageFile) {
        payload.append(
          'image',
          imageFile
        );
      }

      const response = await fetch(
        '/api/cake/products',
        {
          method: 'POST',

          headers: {
            Authorization:
              `Bearer ${token}`,
          },

          body: payload,
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            'Failed to add cake'
        );
      }

      setMessage(
        'Cake added successfully.'
      );

      setMessageType('success');

      setTimeout(() => {
        navigate('/cake/cakes');
      }, 900);

    } catch (error) {
      console.error(
        'Add cake error:',
        error
      );

      setMessage(
        error.message ||
          'Failed to add cake'
      );

      setMessageType('error');

    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cake-add-page">

      {/* SIDEBAR */}

      <aside className="cake-add-sidebar">

        <div className="cake-add-brand">

          <div className="cake-add-logo">
            🎂
          </div>

          <div>
            <h2>VAKRA CAKE</h2>
            <span>Vendor Panel</span>
          </div>

        </div>


        <nav className="cake-add-menu">

          <button
            type="button"
            onClick={() =>
              navigate('/cake/vendor')
            }
          >
            <span>🏠</span>
            Home
          </button>


          <button
            type="button"
            className="active"
            onClick={() =>
              navigate('/cake/cakes')
            }
          >
            <span>🎂</span>
            Cakes
          </button>


          <button type="button">
            <span>📦</span>
            Orders
          </button>


          <button type="button">
            <span>👥</span>
            Customers
          </button>


          <button type="button">
            <span>🧾</span>
            Billing
          </button>


          <button type="button">
            <span>📊</span>
            Records
          </button>


          <button type="button">
            <span>⚙️</span>
            Settings
          </button>

        </nav>


        <div className="cake-add-sidebar-bottom">

          <button
            type="button"
            className="cake-add-logout"
            onClick={handleLogout}
          >
            <span>↪</span>
            Logout
          </button>

        </div>

      </aside>


      {/* MAIN */}

      <main className="cake-add-main">

        {/* TOPBAR */}

        <header className="cake-add-topbar">

          <div>

            <button
              type="button"
              className="cake-add-back-button"
              onClick={() =>
                navigate('/cake/cakes')
              }
            >
              ← Back to Cakes
            </button>

            <h3>Add Cake</h3>

            <p>
              Create a new cake product for your shop.
            </p>

          </div>


          <div className="cake-add-profile">

            <div className="cake-add-profile-icon">

              {vendor.shop_name
                ? vendor.shop_name
                    .charAt(0)
                    .toUpperCase()
                : 'C'}

            </div>

            <div className="cake-add-profile-info">

              <strong>
                {vendor.shop_name ||
                  'Cake Shop'}
              </strong>

              <span>
                {vendor.vendor_code ||
                  'VAKRA Vendor'}
              </span>

            </div>

          </div>

        </header>


        {/* CONTENT */}

        <section className="cake-add-content">

          <div className="cake-add-heading">

            <span>
              PRODUCT MANAGEMENT
            </span>

            <h1>
              Add New Cake
            </h1>

            <p>
              Add basic details, price,
              variants, flavors and
              customization options.
            </p>

          </div>


          {message && (
            <div
              className={
                messageType === 'success'
                  ? 'cake-add-message success'
                  : 'cake-add-message error'
              }
            >
              {message}
            </div>
          )}


          <form
            className="cake-add-form"
            onSubmit={handleSubmit}
          >

            {/* BASIC DETAILS */}

            <div className="cake-add-card">

              <div className="cake-add-card-heading">

                <div>
                  <h2>
                    Basic Details
                  </h2>

                  <p>
                    Main information about
                    this cake.
                  </p>
                </div>

              </div>


              <div className="cake-add-grid">

                <div className="cake-add-group cake-add-full">

                  <label>
                    Cake Name
                    <span>*</span>
                  </label>

                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Example: Chocolate Truffle Cake"
                  />

                </div>


                <div className="cake-add-group">

                  <label>
                    Category
                    <span>*</span>
                  </label>

                  <select
                    name="category"
                    value={
                      formData.category
                    }
                    onChange={handleChange}
                  >
                    <option value="">
                      Select category
                    </option>

                    {categories.map(
                      (category) => (
                        <option
                          key={category}
                          value={category}
                        >
                          {category}
                        </option>
                      )
                    )}

                  </select>

                </div>


                <div className="cake-add-group">

                  <label>
                    Starting Price
                    <span>*</span>
                  </label>

                  <div className="cake-add-price-field">

                    <span>₹</span>

                    <input
                      type="number"
                      name="starting_price"
                      value={
                        formData.starting_price
                      }
                      onChange={handleChange}
                      placeholder="499"
                      min="0"
                    />

                  </div>

                </div>


                <div className="cake-add-group cake-add-full">

                  <label>
                    Description
                  </label>

                  <textarea
                    name="description"
                    value={
                      formData.description
                    }
                    onChange={handleChange}
                    placeholder="Write a short description about this cake..."
                    rows="4"
                  />

                </div>

              </div>

            </div>


            {/* IMAGE */}

            <div className="cake-add-card">

              <div className="cake-add-card-heading">

                <div>
                  <h2>
                    Cake Image
                  </h2>

                  <p>
                    Add one clear product image.
                  </p>
                </div>

              </div>


              <div className="cake-add-image-area">

                {imagePreview ? (

                  <div className="cake-add-image-preview">

                    <img
                      src={imagePreview}
                      alt="Cake preview"
                    />

                    <button
                      type="button"
                      onClick={removeImage}
                    >
                      Remove Image
                    </button>

                  </div>

                ) : (

                  <label className="cake-add-image-upload">

                    <div>
                      📷
                    </div>

                    <strong>
                      Upload Cake Image
                    </strong>

                    <span>
                      JPG, PNG or WEBP
                      up to 3 MB
                    </span>

                    <input
                      type="file"
                      accept="image/*"
                      onChange={
                        handleImageChange
                      }
                    />

                  </label>

                )}

              </div>

            </div>


            {/* VARIANTS */}

            <div className="cake-add-card">

              <div className="cake-add-card-heading">

                <div>
                  <h2>
                    Weight & Price Variants
                  </h2>

                  <p>
                    Example: 500 gm ₹499,
                    1 kg ₹899.
                  </p>
                </div>


                <button
                  type="button"
                  className="cake-add-small-button"
                  onClick={addVariant}
                >
                  + Add Variant
                </button>

              </div>


              <div className="cake-add-variants">

                {variants.map(
                  (variant, index) => (

                    <div
                      className="cake-add-variant-row"
                      key={index}
                    >

                      <div className="cake-add-group">

                        <label>
                          Weight / Size
                        </label>

                        <input
                          type="text"
                          value={
                            variant.weight
                          }
                          onChange={(e) =>
                            handleVariantChange(
                              index,
                              'weight',
                              e.target.value
                            )
                          }
                          placeholder="Example: 500 gm"
                        />

                      </div>


                      <div className="cake-add-group">

                        <label>
                          Price
                        </label>

                        <div className="cake-add-price-field">

                          <span>₹</span>

                          <input
                            type="number"
                            value={
                              variant.price
                            }
                            onChange={(e) =>
                              handleVariantChange(
                                index,
                                'price',
                                e.target.value
                              )
                            }
                            placeholder="499"
                            min="0"
                          />

                        </div>

                      </div>


                      <button
                        type="button"
                        className="cake-add-remove-row"
                        onClick={() =>
                          removeVariant(index)
                        }
                        disabled={
                          variants.length === 1
                        }
                      >
                        ×
                      </button>

                    </div>

                  )
                )}

              </div>

            </div>


            {/* FLAVORS */}

            <div className="cake-add-card">

              <div className="cake-add-card-heading">

                <div>
                  <h2>
                    Flavors
                  </h2>

                  <p>
                    Add all flavors available
                    for this cake.
                  </p>
                </div>


                <button
                  type="button"
                  className="cake-add-small-button"
                  onClick={addFlavor}
                >
                  + Add Flavor
                </button>

              </div>


              <div className="cake-add-flavor-list">

                {flavors.map(
                  (flavor, index) => (

                    <div
                      className="cake-add-flavor-row"
                      key={index}
                    >

                      <input
                        type="text"
                        value={flavor}
                        onChange={(e) =>
                          handleFlavorChange(
                            index,
                            e.target.value
                          )
                        }
                        placeholder="Example: Chocolate"
                      />

                      <button
                        type="button"
                        onClick={() =>
                          removeFlavor(index)
                        }
                        disabled={
                          flavors.length === 1
                        }
                      >
                        ×
                      </button>

                    </div>

                  )
                )}

              </div>

            </div>


            {/* CAKE OPTIONS */}

            <div className="cake-add-card">

              <div className="cake-add-card-heading">

                <div>
                  <h2>
                    Cake Options
                  </h2>

                  <p>
                    Configure preparation
                    and customization options.
                  </p>
                </div>

              </div>


              <div className="cake-add-grid">

                <div className="cake-add-group">

                  <label>
                    Egg Type
                  </label>

                  <select
                    name="egg_type"
                    value={
                      formData.egg_type
                    }
                    onChange={handleChange}
                  >
                    <option value="eggless">
                      Eggless
                    </option>

                    <option value="egg">
                      With Egg
                    </option>

                    <option value="both">
                      Egg & Eggless Both
                    </option>

                  </select>

                </div>


                <div className="cake-add-group">

                  <label>
                    Shape
                  </label>

                  <select
                    name="shape"
                    value={
                      formData.shape
                    }
                    onChange={handleChange}
                  >
                    <option value="">
                      Select shape
                    </option>

                    {shapes.map(
                      (shape) => (
                        <option
                          key={shape}
                          value={shape.toLowerCase()}
                        >
                          {shape}
                        </option>
                      )
                    )}

                  </select>

                </div>


                <div className="cake-add-group">

                  <label>
                    Availability
                  </label>

                  <select
                    name="availability"
                    value={
                      formData.availability
                    }
                    onChange={handleChange}
                  >
                    <option value="available">
                      Available
                    </option>

                    <option value="out_of_stock">
                      Out of Stock
                    </option>

                  </select>

                </div>

              </div>


              <div className="cake-add-toggle-list">

                <label className="cake-add-toggle-card">

                  <div>

                    <strong>
                      Cake Message
                    </strong>

                    <span>
                      Customer can enter
                      a message for the cake.
                    </span>

                  </div>

                  <input
                    type="checkbox"
                    name="cake_message_enabled"
                    checked={
                      formData.cake_message_enabled
                    }
                    onChange={handleChange}
                  />

                </label>


                <label className="cake-add-toggle-card">

                  <div>

                    <strong>
                      Customization
                    </strong>

                    <span>
                      Allow special customization
                      requests for this cake.
                    </span>

                  </div>

                  <input
                    type="checkbox"
                    name="customization_enabled"
                    checked={
                      formData.customization_enabled
                    }
                    onChange={handleChange}
                  />

                </label>

              </div>

            </div>


            {/* ACTIONS */}

            <div className="cake-add-actions">

              <button
                type="button"
                className="cake-add-cancel"
                onClick={() =>
                  navigate('/cake/cakes')
                }
              >
                Cancel
              </button>


              <button
                type="submit"
                className="cake-add-save"
                disabled={loading}
              >
                {loading
                  ? 'Saving Cake...'
                  : 'Save Cake'}
              </button>

            </div>

          </form>

        </section>

      </main>

    </div>
  );
};

export default CakeAddProduct;
